import { useMemo, useState } from "react";
import API_BASE from "../../config";
import StatusBadge from "../../components/StatusBadge";
import StudentDetailModal from "../../components/StudentDetailModal";
import s from "./SectionRoster.module.css";

const AVATAR_COLORS = ["#5a4e3a","#3a4e4a","#4a3a5a","#4e4a3a","#3a4a5a","#5a3a3a","#3a5a3a"];
function avatarColor(seed) {
  let h = 0;
  for (let i = 0; i < (seed || "").length; i++) h = (h * 31 + seed.charCodeAt(i)) >>> 0;
  return AVATAR_COLORS[h % AVATAR_COLORS.length];
}
function initials(first, last) {
  return ((last?.[0] ?? "") + (first?.[0] ?? "")).toUpperCase() || "?";
}
function fullName(st) {
  return [st.lastName, st.firstName].filter(Boolean).join(", ") || "(Unnamed)";
}

/** Resolve a student's photo to an `<img>`-src or null. The backend stores
 *  either a full URL or a relative path; we leave full URLs untouched and
 *  prefix relative paths with API_BASE. */
function photoSrc(photoUrl) {
  if (!photoUrl) return null;
  if (/^https?:\/\//i.test(photoUrl)) return photoUrl;
  return `${API_BASE}${photoUrl.startsWith("/") ? "" : "/"}${photoUrl}`;
}

/**
 * Roster panel for a section + term, designed to drop into the section-mode
 * GroupDetailModal on the Class Offerings page. Compact strip by default;
 * expandable to a searchable student grid. Read-only — actual roster
 * mutations live on the Directory > Enrollments tab, reachable via the
 * "Manage in Directory" button.
 *
 * Props:
 *   sectionId         number
 *   schoolYearId      number | null   — when null, no SY filter applied
 *   semesterId        number | null   — when null, no semester filter applied
 *   students          Array<Student>  — full students list from parent
 *   enrollments       Array<Enrollment>
 *   onManage          fn() — invoked on "Manage in Directory" / "Add students"
 *   defaultOpen       boolean (default false). Start expanded — useful when
 *                     embedded in a roster-only modal where the disclosure
 *                     pattern would be redundant.
 *   hideDisclosure    boolean (default false). Hide the chevron entirely so
 *                     the panel stays at whatever state defaultOpen set.
 *   hideManageButton  boolean (default false). Hide "Manage in Directory"
 *                     button — useful when the parent renders its own.
 */
export default function SectionRoster({
  sectionId,
  schoolYearId,
  semesterId,
  students,
  enrollments,
  // Optional context arrays — passed through to the StudentDetailModal
  // when a student card is clicked. Without them the modal still works
  // but shows fewer fields (no current-term lookup, no enrollment
  // history names).
  sections    = [],
  gradeLevels = [],
  schoolYears = [],
  semesters   = [],
  rooms       = [],
  onManage,
  defaultOpen     = false,
  hideDisclosure  = false,
  hideManageButton = false,
}) {
  const [open, setOpen]     = useState(defaultOpen);
  const [search, setSearch] = useState("");
  // Click-a-student-card → open detail modal. Modal self-fetches parents.
  const [activeStudent, setActiveStudent] = useState(null);

  // Roster = ENROLLED students whose enrollment row matches the
  // (section, SY, semester) tuple. When SY or semester is unknown we
  // relax the filter so the roster doesn't go silently empty.
  const roster = useMemo(() => {
    const studentsById = new Map((students || []).map(st => [st.id, st]));
    const matches = (enrollments || []).filter(e => {
      if (String(e.sectionId) !== String(sectionId)) return false;
      if (String(e.status || "").toUpperCase() !== "ENROLLED") return false;
      if (schoolYearId && String(e.schoolYearId) !== String(schoolYearId)) return false;
      if (semesterId   && String(e.semesterId)   !== String(semesterId))   return false;
      return true;
    });
    // Dedupe: a student should appear at most once even if there are
    // multiple matching enrollment rows (data hygiene defense).
    const seen = new Set();
    const list = [];
    for (const e of matches) {
      if (seen.has(e.studentId)) continue;
      seen.add(e.studentId);
      const st = studentsById.get(e.studentId);
      if (st) list.push(st);
    }
    list.sort((a, b) => fullName(a).localeCompare(fullName(b)));
    return list;
  }, [sectionId, schoolYearId, semesterId, students, enrollments]);

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return roster;
    return roster.filter(st => {
      const hay = [
        st.studentNumber || "",
        fullName(st),
        st.firstName || "",
        st.lastName || "",
      ].join(" ").toLowerCase();
      return hay.includes(q);
    });
  }, [roster, search]);

  const total = roster.length;

  // Avatar stack preview: first 5 students, then a "+N" tile if more.
  const stackPreview = roster.slice(0, 5);
  const overflow = Math.max(0, total - stackPreview.length);

  return (
    <div className={s.roster}>
      <div className={s.strip}>
        <span className={s.label}>Students</span>
        {total > 0 && (
          <div className={s.stack} aria-hidden="true">
            {stackPreview.map(st => {
              const src = photoSrc(st.photoUrl);
              return (
                <span
                  key={st.id}
                  className={s.stackAvatar}
                  style={src ? { backgroundImage: `url(${src})` }
                             : { backgroundColor: avatarColor(fullName(st)) }}>
                  {!src && initials(st.firstName, st.lastName)}
                </span>
              );
            })}
            {overflow > 0 && (
              <span className={`${s.stackAvatar} ${s.stackMore}`}>+{overflow}</span>
            )}
          </div>
        )}
        <span className={s.count}>
          <span className={s.countNumber}>{total}</span> student{total === 1 ? "" : "s"} enrolled
        </span>
        <span className={s.spacer} />
        {!hideManageButton && (
          <button
            type="button"
            className={s.manageBtn}
            onClick={onManage}>
            Manage Sections →
          </button>
        )}
        {!hideDisclosure && (
          <button
            type="button"
            className={s.disclosureBtn}
            aria-expanded={open}
            aria-controls="section-roster-panel"
            aria-label={open ? "Hide students" : "Show students"}
            onClick={() => setOpen(o => !o)}>
            <span className={s.disclosureChevron}>▾</span>
          </button>
        )}
      </div>

      {open && (
        <div id="section-roster-panel" className={s.panel}>
          {total === 0 ? (
            <div className={s.empty}>
              <div className={s.emptyTitle}>No students enrolled yet</div>
              <div className={s.emptySub}>
                This section has no enrolled students for the current term.
              </div>
              <button type="button" className={s.emptyCta} onClick={onManage}>
                Add students to section
              </button>
            </div>
          ) : (
            <>
              <div className={s.panelTools}>
                <input
                  type="text"
                  className={s.search}
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  placeholder="Search roster by name or student #…"
                  aria-label="Search roster"
                />
                <span className={s.subCount}>
                  Showing {visible.length} of {total}
                </span>
              </div>
              {visible.length === 0 ? (
                <div className={s.empty}>
                  <div className={s.emptyTitle}>No matches</div>
                  <div className={s.emptySub}>
                    Try a different search or clear the filter.
                  </div>
                </div>
              ) : (
                <div className={s.grid}>
                  {visible.map((st, i) => {
                    const src = photoSrc(st.photoUrl);
                    // Stagger card entrance subtly; cap so big rosters
                    // don't feel sluggish on first render.
                    const delay = Math.min(i * 22, 360);
                    return (
                      <button
                        key={st.id}
                        type="button"
                        className={`${s.card} ${s.cardClickable}`}
                        style={{ animationDelay: `${delay}ms` }}
                        onClick={() => setActiveStudent(st)}
                        aria-label={`Open profile for ${fullName(st)}`}>
                        <span
                          className={s.avatar}
                          style={src ? { backgroundImage: `url(${src})` }
                                     : { backgroundColor: avatarColor(fullName(st)) }}>
                          {!src && initials(st.firstName, st.lastName)}
                        </span>
                        <div className={s.cardBody}>
                          <div className={s.cardName}>{fullName(st)}</div>
                          <div className={s.cardMeta}>
                            {st.studentNumber && (
                              <code className={s.numberPill}>{st.studentNumber}</code>
                            )}
                            <StatusBadge status={st.status} />
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </>
          )}
        </div>
      )}

      <StudentDetailModal
        open={!!activeStudent}
        student={activeStudent}
        sections={sections}
        gradeLevels={gradeLevels}
        schoolYears={schoolYears}
        semesters={semesters}
        rooms={rooms}
        enrollments={enrollments}
        onClose={() => setActiveStudent(null)}
      />
    </div>
  );
}
